import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Paginado } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import {
  Campo,
  Carregando,
  ErroBox,
  LinhaCampos,
  Modal,
  ModalRodape,
  PageHeader,
} from '../components/ui';
import { CampoTelefone } from '../components/CampoTelefone';
import { Balao, type Mensagem } from './Conversas';
import { ROTULOS_STATUS, cidadeDoEvento, formatarPeriodo } from './Eventos';
import { ESTADOS as ESTADOS_DA_JORNADA } from './Jornadas';
import { ehTipoDeConexao, rotuloDoTipo, type TipoDeConexao } from '@mpa/shared/tipos-de-conexao';

/**
 * O relatório do participante.
 *
 * ⚠️ **Em 12/10/2026 esta tela foi redesenhada para ser o mesmo relatório do
 * sistema irmão** (triagem de candidatos), trocando o eixo: participante × evento
 * no lugar de candidato × vaga. Duas colunas numa página, e não abas — à esquerda
 * quem a pessoa é e o que de fato aconteceu (cadastro, consentimento, por onde a
 * conversa passou), à direita **só** o que a análise concluiu.
 *
 * A fronteira é registro × conclusão, não "cadastro × IA": `Habilidades` e
 * `Idiomas` saem da IA e mesmo assim ficam na esquerda, porque são fatos
 * extraídos, não juízos.
 *
 * A separação não é estética. Quem abre este relatório faz duas leituras ao mesmo
 * tempo: confere a identidade (é a pessoa certa? o contato está aqui?) e lê a
 * análise (vale conectar? com quem?). Em abas, a segunda esconde a primeira, e é
 * preciso pular de um lado ao outro para conferir se a conclusão fala da pessoa
 * que está na tela.
 *
 * **O que é NOTA não é calculado aqui.** A chance de conexão, a sugestão do
 * parecer e a pontuação comportamental vêm prontas da API, em `analise` — regra
 * fixa, testada em `parecer.spec.ts`. Calcular no React faria
 * a regra existir em dois lugares, e o segundo não tem teste: `@mpa/web` não tem
 * suíte. **Esta tela exibe; ela não decide.**
 */

/* ─────────── O que vem da API ─────────── */

interface Bloco {
  value: unknown;
  source: string;
  confidence: number;
  collectedAt: string;
  confirmed: boolean;
}

interface Dimensao {
  nota: number;
  obs: string;
}

interface Compatibilidade {
  ritmo: string;
  decisao: string;
  comunicacao: string;
  risco: string;
  trabalhaBemCom: string[];
  atritoCom: string[];
}

interface PerfilPsicologico {
  perfil?: { arquetipo: string; mbti: string; inteligencias: string; lideranca: string };
  motivacoes?: {
    autonomia: number;
    competencia: number;
    pertencimento: number;
    obs: string;
    motivadorPrincipal: string;
  };
  comportamental?: Record<string, Dimensao>;
  /** Desde 27/10/2026. Vazio é "a conversa não mostrou". Ver `ComoTrabalhaComOsOutros`. */
  compatibilidade?: Compatibilidade;
  confianca: number;
  /** Formato anterior a 12/10/2026 — cinco campos de prosa. Ver `ComoTrabalhavaAntes`. */
  comunicacao?: string;
  decisao?: string;
  motivacao?: string;
  colaboracao?: string;
  ritmo?: string;
}

interface Notas {
  assuntosParaComecar?: string[];
  pontosDeAtencao?: string[];
  resumoExecutivo?: string;
  maiorDiferencial?: string;
  pontoAtencao?: string;
}

interface IdentidadeConsolidada {
  idiomas: { idioma: string; nivel: string }[];
  experienciaProfissional: { empresa: string; cargo: string; periodo: string; destaques: string }[];
  formacao: { instituicao: string; curso: string; periodo: string }[];
  /** Desde 28/10/2026. Ausentes em quem foi sintetizado antes. */
  projetoPrincipal?: { nome: string; oQueE: string; estagio: string; oQueFalta: string[] };
  outrosProjetos?: { nome: string; oQueE: string }[];
  valores?: { valor: string; evidencia: string }[];
  /** Os cinco tipos de conexão, desde 03/11/2026. Ver `@mpa/shared/tipos-de-conexao`. */
  tiposDeConexao?: {
    procura?: { tipo: string; evidencia: string }[];
    atende?: { tipo: string; evidencia: string }[];
  };
}

interface Perfil extends Partial<Record<string, unknown>> {
  aiSummary: string | null;
  psychologicalProfile: PerfilPsicologico | null;
  synthesisNotes: Notas | null;
  synthesizedAt: string | null;
  participantProfile: IdentidadeConsolidada | null;
  completeness: number;
  completenessBreakdown: Record<string, { peso: number; preenchido: boolean }> | null;
  lastUpdatedAt: string;
}

/** Calculado no servidor, nunca aqui. Ver o aviso no topo do arquivo. */
interface Analise {
  /**
   * O que o cruzamento encontrou para ela — contagem, não estimativa. No lugar da
   * aderência ao evento desde 27/10/2026: ver `parecer.ts` na API.
   */
  chance: {
    possiveis: number;
    melhor: number | null;
    avaliados: number;
    corte: number;
    motivo: 'perfil_raso' | 'sem_outros_perfis' | 'sem_cruzamento' | null;
  };
  objetivos: { indice: number; kind: string; content: string }[];
  pontuacaoComportamental: number | null;
  sugestao: 'prioridade' | 'pronto' | 'com_ressalvas' | 'perfil_raso' | 'fora';
}

interface Participante {
  id: string;
  personId: string;
  fullName: string;
  phoneE164: string;
  email: string | null;
  city: string | null;
  country: string | null;
  linkedin: string | null;
  optOutAt: string | null;
  jobTitle: string | null;
  company: string | null;
  sector: string | null;
  segment: string | null;
  status: string;
  legalBasis: string | null;
  notes: string | null;
  createdAt: string;
  event: {
    id: string;
    name: string;
    status: string;
    shortDescription: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    startDate: string | null;
    endDate: string | null;
  };
  /**
   * O que a pessoa quer DESTE evento, nas palavras dela — escrito pela síntese
   * desde 13/10/2026. É fato extraído, não juízo; por isso mora na esquerda, com
   * a participação. Lista, ou o formato antigo `{objetivos}`, que ninguém gravou.
   */
  eventGoals?: string[] | { objetivos?: string[] } | null;
  tags: { id: string; tag: string; source: string; confidence: number | null }[];
  conversation: { id: string } | null;
  journey: {
    status: string;
    currentStage: string | null;
    startedAt: string | null;
    completedAt: string | null;
    steps: {
      id: string;
      stage: string;
      sequence: number;
      completed: boolean;
      completedAt: string | null;
    }[];
  } | null;
  profile: Perfil | null;
  consents: {
    id: string;
    purpose: string;
    response: string | null;
    grantedAt: string | null;
    revokedAt: string | null;
  }[];
  participations: {
    id: string;
    eventId: string;
    event: { name: string; startDate: string | null; status: string };
  }[];
  analise: Analise;
}

interface Peca {
  rotulo: string;
  texto: string;
  fonte: string;
  mediaType: string;
  criadaEm: string;
  /** O material é da PESSOA e atravessa eventos — este é o de origem. */
  eventId: string;
  evento: string;
}

interface ParDePergunta {
  pergunta: string;
  resposta: string;
  /** O tema da pergunta, desde 28/10/2026. Ausente no roteiro antigo. */
  tema?: string;
  eventId: string;
  evento: string;
}

/** Os temas das perguntas, como a API os grava. Ver `TEMAS_DAS_PERGUNTAS`. */
const NOMES_DOS_TEMAS: Record<string, string> = {
  projetos: 'Projetos',
  projeto_principal: 'Projeto principal',
  valores: 'Valores',
};

interface Material {
  pecas: Peca[];
  perguntasERespostas: ParDePergunta[];
  /** `ordem` só em jornadas anteriores a 13/10/2026 — e aí a tela avisa. */
  pareamento: 'tempo' | 'ordem';
  /** Peças de eventos fora do acesso deste usuário: a síntese as viu, a tela não. */
  ocultas: number;
}

interface Indicacao {
  id: string;
  status: string;
  score: number | null;
  justification: string | null;
  rotulo: string;
  suggested: {
    person: { fullName: string };
    jobTitle: string | null;
    company: string | null;
  } | null;
}

/* ─────────── Vocabulário ─────────── */

/**
 * Os onze blocos, na ordem do peso que cada um tem na completude.
 *
 * Duas chaves por bloco, e não é descuido: `completenessBreakdown` é indexado
 * pelas chaves em português de `PESOS` (`apps/api/.../perfil.service.ts`), e as
 * colunas de `PersonProfile` têm nome em inglês.
 */
const BLOCOS: { chave: string; coluna: string; rotulo: string }[] = [
  { chave: 'necessidades', coluna: 'needs', rotulo: 'O que busca' },
  { chave: 'ofertas', coluna: 'offers', rotulo: 'O que oferece' },
  { chave: 'perfilDeNegocios', coluna: 'businessProfile', rotulo: 'Perfil de negócios' },
  { chave: 'profissional', coluna: 'professional', rotulo: 'Profissional' },
  { chave: 'competencias', coluna: 'competencies', rotulo: 'Competências' },
  { chave: 'trajetoria', coluna: 'trajectory', rotulo: 'Trajetória' },
  { chave: 'interesses', coluna: 'interests', rotulo: 'Interesses' },
  { chave: 'desafios', coluna: 'challenges', rotulo: 'Desafios' },
  { chave: 'mercados', coluna: 'markets', rotulo: 'Mercados' },
  { chave: 'paisesDeInteresse', coluna: 'countriesOfInterest', rotulo: 'Países de interesse' },
  { chave: 'temas', coluna: 'themes', rotulo: 'Temas' },
];

/** Os blocos que já têm seção própria e não se repetem na lista geral. */
const BLOCOS_COM_SECAO = ['competencies', 'trajectory', 'professional'];

const FONTES: Record<string, string> = {
  relato_audio: 'do relato gravado',
  curriculo: 'do currículo',
  linkedin: 'do perfil público',
  resposta_texto: 'de uma resposta escrita',
};

/** As oito dimensões, na ordem do sistema irmão. */
const DIMENSOES: { chave: string; rotulo: string }[] = [
  { chave: 'autenticidade', rotulo: 'Autenticidade' },
  { chave: 'autoconhecimento', rotulo: 'Autoconhecimento' },
  { chave: 'inteligenciaEmocional', rotulo: 'Inteligência emocional' },
  { chave: 'visaoEstrategica', rotulo: 'Visão estratégica' },
  { chave: 'networking', rotulo: 'Potencial de networking' },
  { chave: 'adaptabilidade', rotulo: 'Adaptabilidade' },
  { chave: 'lideranca', rotulo: 'Liderança' },
  { chave: 'consistencia', rotulo: 'Consistência' },
];

const ETAPAS: Record<string, string> = {
  consentimento: 'Consentimento',
  linkedin: 'LinkedIn',
  linkedin_url: 'LinkedIn (endereço)',
  curriculo: 'Currículo',
  // O passo gravado é `tipos`; a etapa em curso, `tipos_de_conexao` (03/11/2026).
  tipos: 'Tipos de conexão',
  tipos_de_conexao: 'Tipos de conexão',
  relato: 'Relato',
  perguntas: 'Perguntas',
  encerramento: 'Encerramento',
};

const FINALIDADES: Record<string, string> = {
  jornada_evento: 'Participar da jornada do evento',
  compartilhar_telefone: 'Compartilhar o telefone numa recomendação',
};

/**
 * A sugestão do parecer, que vem calculada do servidor.
 *
 * O paralelo do `ExecutiveOpinion` do sistema irmão, e a pergunta que ela responde
 * é a daqui: **vale cruzar esta pessoa?**
 */
const SUGESTOES: Record<Analise['sugestao'], { rotulo: string; cor: string; ajuda: string }> = {
  prioridade: {
    rotulo: 'Prioridade para conexões',
    cor: 'success',
    ajuda: 'Já tem conexão forte neste evento e perfil bem preenchido — é por quem se começa uma rodada.',
  },
  pronto: {
    rotulo: 'Pronto para cruzamento',
    cor: 'info',
    ajuda: 'Tem perfil suficiente, e nada pede atenção antes de aprovar.',
  },
  com_ressalvas: {
    rotulo: 'Cruzar com ressalvas',
    cor: 'warning',
    ajuda: 'Há ponto de atenção registrado — leia antes de aprovar.',
  },
  perfil_raso: {
    rotulo: 'Perfil raso — não cruzar',
    cor: 'muted',
    ajuda: 'A conversa ainda trouxe pouco sobre esta pessoa. Não entra em recomendação.',
  },
  fora: {
    rotulo: 'Fora do cruzamento',
    cor: 'danger',
    ajuda: 'Pediu para sair, não autorizou, ou a jornada foi recusada ou cancelada.',
  },
};

/**
 * A cor do número da chance — só a cor; a contagem vem pronta do servidor.
 *
 * Verde com uma conexão forte (80 ou mais, a régua de "prioridade" em
 * `parecer.ts`), amarelo com alguma acima do corte, vermelho com nenhuma.
 */
function corDaChance(chance: Analise['chance']): 'A' | 'B' | 'C' {
  if (chance.melhor !== null && chance.melhor >= 80) return 'A';
  if (chance.possiveis > 0) return 'B';
  return 'C';
}

const MOTIVOS_SEM_CHANCE: Record<string, string> = {
  perfil_raso:
    'Sabemos pouco demais sobre esta pessoa para cruzá-la com as outras. A chance aparece quando a conversa trouxer o suficiente.',
  sem_outros_perfis:
    'Ainda não há outra pessoa deste evento com perfil para cruzar. A chance aparece quando mais participantes terminarem a jornada.',
  sem_cruzamento:
    'Há com quem cruzar, mas o cruzamento ainda não rodou para esta pessoa. Ele roda sozinho depois da síntese, ou pela página de conexões do evento.',
};

/* ─────────── A tela ─────────── */

export function Perfil() {
  const { id = '' } = useParams();
  const [peca, setPeca] = useState<Peca | null>(null);
  const [transcricao, setTranscricao] = useState(false);
  const [entrevista, setEntrevista] = useState(false);

  const consulta = useQuery({
    queryKey: ['participante', id],
    queryFn: () => api<Participante>(`/participants/${id}`),
  });

  const material = useQuery({
    queryKey: ['participante', id, 'material'],
    queryFn: () => api<Material>(`/participants/${id}/material`),
  });

  if (consulta.isPending) return <Carregando />;
  if (consulta.isError) {
    return <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />;
  }

  const p = consulta.data!;

  return (
    <>
      <PageHeader
        titulo={p.fullName}
        subtitulo={
          <>
            {[p.jobTitle, p.company].filter(Boolean).join(' · ') || 'Sem cargo cadastrado'}
            {' · '}
            <Link to={`/eventos/${p.event.id}`}>{p.event.name}</Link>
          </>
        }
        voltar={{ para: '/participantes', texto: 'Participantes' }}
        acoes={<AcoesDoTitular participante={p} />}
      />

      {p.optOutAt && (
        <div className="aviso aviso-danger" style={{ marginBottom: 16 }}>
          <i className="ti ti-bell-off" />
          <span>
            Esta pessoa pediu para não receber mais mensagens em{' '}
            {new Date(p.optOutAt).toLocaleDateString('pt-BR')}. O perfil continua valendo; falar
            com ela de novo exige consentimento novo.
          </span>
        </div>
      )}

      <BarraDoMaterial
        participante={p}
        material={material.data}
        carregando={material.isPending}
        aoAbrirPeca={setPeca}
        aoAbrirEntrevista={() => setEntrevista(true)}
        aoAbrirTranscricao={() => setTranscricao(true)}
      />

      <div className="relatorio">
        <div className="relatorio-coluna">
          <Identidade participante={p} />
          <Contato participante={p} />
          <Habilidades perfil={p.profile} />
          <Idiomas perfil={p.profile} />
          <Participacao participante={p} />
          <Pipeline participante={p} />
          <Consentimento participante={p} />
          <OutrosEventos participante={p} />
          <SobreOEvento participante={p} />
          <Documentos material={material.data} eventoAtual={p.event.id} aoAbrirPeca={setPeca} />
        </div>

        <div className="relatorio-coluna">
          <ParecerExecutivo participante={p} />
          <ChanceDeConexao participante={p} />
          <AnalisePsicologica participante={p} />
          <Conexoes participante={p} />
          <Atencao perfil={p.profile} />
          <Resumo perfil={p.profile} />
          <ProjetosEValores perfil={p.profile} />
          <Experiencia perfil={p.profile} />
          <Formacao perfil={p.profile} />
          <BlocosRestantes perfil={p.profile} />
          <Etiquetas participante={p} />
          <NotasInternas participante={p} />
        </div>
      </div>

      <Consideracoes />

      {transcricao && p.conversation && (
        <TranscricaoModal
          conversationId={p.conversation.id}
          material={material.data}
          aoFechar={() => setTranscricao(false)}
        />
      )}

      {entrevista && material.data && (
        <EntrevistaModal
          material={material.data}
          eventoAtual={p.event.id}
          aoFechar={() => setEntrevista(false)}
        />
      )}

      {peca && <PecaModal peca={peca} aoFechar={() => setPeca(null)} />}
    </>
  );
}

/* ─────────── Andaimes ─────────── */

/** Um cartão com título. A tela é feita de dezenas deles, nas duas colunas. */
function Cartao({
  titulo,
  icone,
  descricao,
  destaque,
  acao,
  children,
}: {
  titulo: string;
  icone?: string;
  descricao?: string;
  destaque?: boolean;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={`card cartao-relatorio${destaque ? ' destaque' : ''}`}>
      <div className="cartao-cabeca">
        <h2 className="cartao-titulo">
          {icone && <i className={`ti ${icone}`} />} {titulo}
        </h2>
        {acao}
      </div>
      {descricao && <p className="cartao-descricao">{descricao}</p>}
      {children}
    </section>
  );
}

/**
 * Uma lista de pílulas que não vira parede.
 *
 * ⚠️ **Existe por um dado real: `competencias` tem 54 itens em produção**, para um
 * teto de extração de 20 — os blocos acumulam a cada material que chega
 * (`fundirBloco`), e nada apara a lista depois.
 *
 * E não é só estética. Com as 54 despejadas, os quatro primeiros do perfil atual
 * são do Bill Gates — resíduo do perfil quimera — e ninguém percebe, porque
 * ninguém lê 54 pílulas. Lista que não cabe na tela é lista que ninguém audita.
 */
function Pilulas({ itens, teto = 12 }: { itens: string[]; teto?: number }) {
  const [tudo, setTudo] = useState(false);
  const sobra = itens.length - teto;
  const visiveis = tudo ? itens : itens.slice(0, teto);

  return (
    <div className="valor-lista">
      {visiveis.map((item, i) => (
        <span key={`${item}-${i}`} className="tema-chip">
          {item}
        </span>
      ))}
      {sobra > 0 && (
        <button className="chip-mais" onClick={() => setTudo(!tudo)}>
          {tudo ? 'mostrar menos' : `+${sobra} ${sobra === 1 ? 'outra' : 'outras'}`}
        </button>
      )}
    </div>
  );
}

/** Uma barra de 0 a 10, como as oito dimensões do sistema irmão. */
function BarraDeNota({ rotulo, nota, obs }: { rotulo: string; nota: number; obs?: string }) {
  return (
    <div className="nota-linha">
      <div className="nota-cabeca">
        <span className="nota-rotulo">{rotulo}</span>
        <span className="nota-trilho">
          <span className="nota-preenchida" style={{ width: `${Math.max(2, nota * 10)}%` }} />
        </span>
        <span className="nota-valor">{nota}</span>
      </div>
      {/*
        A observação vem SEMPRE junto da nota, e é por isso que ela é obrigatória
        no schema. "Liderança: 4" é um número que ninguém consegue contestar;
        "fala de 'nós' mesmo descrevendo o que fez sozinha" pode ser conferido
        contra a transcrição.
      */}
      {obs && <div className="nota-obs">{obs}</div>}
    </div>
  );
}

function listaDoBloco(perfil: Perfil | null, coluna: string): string[] {
  const bloco = perfil?.[coluna] as Bloco | undefined;
  const valor = bloco?.value;
  if (!Array.isArray(valor)) return [];
  return valor.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)));
}

/* ─────────── Coluna esquerda: quem a pessoa é ─────────── */

function Identidade({ participante: p }: { participante: Participante }) {
  const { chance } = p.analise;

  return (
    <section className="card cartao-identidade">
      <div className="identidade-topo">
        <div className="avatar-grande">{iniciais(p.fullName)}</div>
        <div>
          <div className="identidade-nome">{p.fullName}</div>
          <div className="identidade-cargo">
            {[p.jobTitle, p.company].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
      </div>

      {/*
        O número grande é a CHANCE DE CONEXÃO: quantas pessoas deste evento o
        cruzamento encontrou para ela acima do corte. Até 27/10/2026 era a
        aderência ao evento, no molde do Índice do sistema irmão — que responde
        "quanto esta pessoa encaixa", e não "vai sair conexão para ela".

        A completude do perfil saiu da tela em 15/10/2026: é uma soma de pesos
        nossos, que ninguém mediu, e um "98%" lido como qualidade dizia mais do que
        sabe. Continua calculada — o cruzamento usa o corte mínimo.
      */}
      {chance.motivo === null ? (
        <div className="indice-bloco">
          <div className={`indice-numero faixa-${corDaChance(chance)}`}>{chance.possiveis}</div>
          <div>
            <div className="indice-rotulo">
              {chance.possiveis === 1 ? 'conexão possível' : 'conexões possíveis'}
            </div>
            {chance.melhor !== null && <div className="nota-pe">a melhor com {chance.melhor}</div>}
          </div>
        </div>
      ) : (
        <div className="indice-bloco">
          <div className="indice-numero vazio">—</div>
          <div className="indice-rotulo">Chance de conexão ainda sem cálculo</div>
        </div>
      )}

      {p.profile && (
        <div className="identidade-rodape nota-pe">
          Perfil atualizado em {new Date(p.profile.lastUpdatedAt).toLocaleString('pt-BR')}
        </div>
      )}
    </section>
  );
}

function Contato({ participante: p }: { participante: Participante }) {
  const { pode } = useAuth();
  const [editando, setEditando] = useState(false);

  // Quem não vê o contato inteiro (sem `read_pii`) recebe telefone e e-mail
  // mascarados — e um formulário preenchido com a máscara gravaria a máscara.
  const podeEditar = pode('participants.update') && pode('participants.read_pii');

  return (
    <Cartao
      titulo="Contato"
      icone="ti-address-book"
      acao={
        podeEditar && (
          <button className="btn btn-secondary btn-pequeno" onClick={() => setEditando(true)}>
            <i className="ti ti-pencil" /> Editar
          </button>
        )
      }
    >
      <dl className="ficha">
        <Linha rotulo="WhatsApp">
          <span className="mono">{p.phoneE164}</span>
        </Linha>
        <Linha rotulo="E-mail">{p.email}</Linha>
        <Linha rotulo="Cidade">{[p.city, p.country].filter(Boolean).join(' · ')}</Linha>
        <Linha rotulo="LinkedIn">
          {p.linkedin && (
            <a href={p.linkedin} target="_blank" rel="noreferrer">
              abrir perfil
            </a>
          )}
        </Linha>
        <Linha rotulo="Setor">{[p.sector, p.segment].filter(Boolean).join(' · ')}</Linha>
      </dl>

      {editando && <EditarContato participante={p} aoFechar={() => setEditando(false)} />}
    </Cartao>
  );
}

/** Jornada nestes estados ainda vai mandar mensagem — trocar o número muda o destino. */
const JORNADA_EM_CURSO = [
  'AGENDADA',
  'INICIANDO',
  'CONVITE_ENVIADO',
  'AGUARDANDO_RESPOSTA',
  'EM_ANDAMENTO',
  'INATIVO_TEMPORARIO',
];

/**
 * Corrigir os dados de alguém: celular trocado, e-mail novo, nome escrito errado.
 *
 * Não existia até 16/10/2026 — a única forma de mudar um nome era um script no
 * servidor. Vai só o que mudou: mandar o formulário inteiro reescreveria cada
 * campo e deixaria a auditoria dizendo que tudo foi alterado.
 *
 * Campo opcional apagado vai como `null`, que a API entende como "ficou vazio".
 */
function EditarContato({
  participante: p,
  aoFechar,
}: {
  participante: Participante;
  aoFechar: () => void;
}) {
  const cliente = useQueryClient();

  const atual: Record<string, string> = {
    fullName: p.fullName,
    phone: p.phoneE164,
    email: p.email ?? '',
    city: p.city ?? '',
    linkedin: p.linkedin ?? '',
    company: p.company ?? '',
    jobTitle: p.jobTitle ?? '',
  };

  const salvar = useMutation({
    mutationFn: (corpo: Record<string, string | null>) =>
      api(`/participants/${p.id}`, { method: 'PATCH', body: corpo }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['participante', p.id] });
      cliente.invalidateQueries({ queryKey: ['participantes'] });
      cliente.invalidateQueries({ queryKey: ['pessoas'] });
      aoFechar();
    },
  });

  const outrosEventos = p.participations.length;
  const jornadaEmCurso = !!p.journey && JORNADA_EM_CURSO.includes(p.journey.status);

  return (
    <Modal titulo="Editar dados" descricao={p.fullName} aoFechar={aoFechar} largura={560}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const dados = new FormData(e.currentTarget);
          const corpo: Record<string, string | null> = {};

          for (const [campo, antes] of Object.entries(atual)) {
            const agora = String(dados.get(campo) ?? '').trim();
            // O telefone volta do campo como "+55…" e está gravado sem o "+":
            // compara os dígitos, senão todo envio "trocaria" o número.
            const igual =
              campo === 'phone' ? agora.replace(/\D/g, '') === antes.replace(/\D/g, '') : agora === antes;
            if (!igual) corpo[campo] = agora === '' ? null : agora;
          }

          if (!Object.keys(corpo).length) return aoFechar();
          salvar.mutate(corpo);
        }}
      >
        {salvar.isError && <ErroBox erro={salvar.error} />}

        {(outrosEventos > 0 || jornadaEmCurso) && (
          <div className="aviso" style={{ marginBottom: 16 }}>
            <i className="ti ti-info-circle" />
            <span>
              {outrosEventos > 0 &&
                `Os dados são da pessoa: a mudança vale também para ${
                  outrosEventos === 1 ? 'o outro evento' : `os outros ${outrosEventos} eventos`
                } dela. `}
              {jornadaEmCurso &&
                'A jornada dela está em andamento — trocando o WhatsApp, as próximas mensagens vão para o número novo.'}
            </span>
          </div>
        )}

        <Campo label="Nome completo" name="fullName" obrigatorio valorInicial={atual.fullName} />

        <LinhaCampos>
          <CampoTelefone obrigatorio valorInicial={p.phoneE164} />
          <Campo label="E-mail" name="email" tipo="email" opcional valorInicial={atual.email} />
        </LinhaCampos>

        <LinhaCampos>
          <Campo label="Empresa" name="company" opcional valorInicial={atual.company} />
          <Campo label="Cargo" name="jobTitle" opcional valorInicial={atual.jobTitle} />
        </LinhaCampos>

        <LinhaCampos>
          <Campo label="Cidade" name="city" opcional valorInicial={atual.city} />
          <Campo
            label="LinkedIn"
            name="linkedin"
            tipo="url"
            opcional
            placeholder="https://www.linkedin.com/in/…"
            valorInicial={atual.linkedin}
          />
        </LinhaCampos>

        <ModalRodape>
          <button type="button" className="btn btn-ghost" onClick={aoFechar}>
            Cancelar
          </button>
          <button className="btn btn-primary" type="submit" disabled={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </ModalRodape>
      </form>
    </Modal>
  );
}

function Habilidades({ perfil }: { perfil: Perfil | null }) {
  const competencias = listaDoBloco(perfil, 'competencies');
  if (!competencias.length) return null;

  return (
    <Cartao
      titulo="Habilidades"
      icone="ti-bulb"
      descricao={`${competencias.length} identificadas no material que ela enviou.`}
    >
      <Pilulas itens={competencias} />
    </Cartao>
  );
}

function Idiomas({ perfil }: { perfil: Perfil | null }) {
  const idiomas = perfil?.participantProfile?.idiomas ?? [];

  return (
    <Cartao titulo="Idiomas" icone="ti-language">
      {idiomas.length ? (
        <dl className="ficha">
          {idiomas.map((i) => (
            <Linha key={i.idioma} rotulo={i.idioma}>
              <Tag>{i.nivel}</Tag>
            </Linha>
          ))}
        </dl>
      ) : (
        <SemSintese perfil={perfil} oQue="os idiomas" />
      )}
    </Cartao>
  );
}

/** `eventGoals` em lista, nos dois formatos que o campo `Json` admite. */
function objetivosDe(bruto: Participante['eventGoals']): string[] {
  if (Array.isArray(bruto)) return bruto.map(String);
  return Array.isArray(bruto?.objetivos) ? bruto.objetivos.map(String) : [];
}

/** O cadastro desta participação, em palavras — o banco guarda o código. */
const SITUACOES_DO_CADASTRO: Record<string, string> = {
  ATIVO: 'Cadastro ativo',
  INATIVO: 'Cadastro inativo',
  DUPLICADO: 'Cadastro duplicado',
  EXCLUIDO: 'Cadastro excluído',
  BLOQUEADO: 'Cadastro bloqueado',
};

const dataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * A participação no formato do "Sobre a Vaga", como o "Sobre o evento" (16/10/2026):
 * linhas com ícone em vez de ficha com rótulo à esquerda. Linha sem valor não
 * aparece — começo e término só existem depois que a jornada anda.
 */
function Participacao({ participante: p }: { participante: Participante }) {
  const j = p.journey;
  const objetivos = objetivosDe(p.eventGoals);
  const etapa = j?.currentStage
    ? (ETAPAS[j.currentStage] ?? j.currentStage)
    : j
      ? 'aguardando a primeira resposta'
      : null;

  return (
    <Cartao titulo="Participação">
      {objetivos.length > 0 && (
        <p className="sobre-texto">Quer deste evento: {objetivos.join('; ')}</p>
      )}

      <ul className="sobre-linhas">
        <li>
          <i className="ti ti-calendar-event" />{' '}
          <Link to={`/eventos/${p.event.id}`}>{p.event.name}</Link>
        </li>
        <li>
          <i className="ti ti-user-check" /> {SITUACOES_DO_CADASTRO[p.status] ?? p.status}
        </li>
        <li>
          <i className="ti ti-route" />{' '}
          {j
            ? `Jornada: ${ESTADOS_DA_JORNADA[j.status]?.rotulo ?? j.status}${etapa ? ` · ${etapa}` : ''}`
            : 'Jornada não iniciada'}
        </li>
        <li>
          <i className="ti ti-clock" /> Cadastrado em{' '}
          {new Date(p.createdAt).toLocaleDateString('pt-BR')}
        </li>
        {j?.startedAt && (
          <li>
            <i className="ti ti-player-play" /> Jornada começou em {dataHora(j.startedAt)}
          </li>
        )}
        {j?.completedAt && (
          <li>
            <i className="ti ti-flag-check" /> Jornada terminou em {dataHora(j.completedAt)}
          </li>
        )}
      </ul>
    </Cartao>
  );
}

/**
 * A linha do tempo da jornada — o "Histórico do Pipeline" do sistema irmão.
 *
 * `JourneyStep` existe desde a fase 2 e **nenhuma rota o expunha**: a conversa
 * guardava por onde passou e ninguém conseguia ver.
 *
 * ⚠️ **Mora na esquerda, e no sistema irmão mora na direita.** Lá a direita é "a
 * análise" num sentido largo; aqui ela é só o que a IA concluiu. Por onde a
 * conversa passou não é conclusão de nada — é registro, do mesmo tipo que a
 * `Participacao` logo acima, que diz a etapa atual. Separadas por uma coluna, as
 * duas obrigavam a atravessar a tela para conferir se o "Encerramento" que a
 * Participação anuncia já tinha mesmo acontecido.
 */
function Pipeline({ participante: p }: { participante: Participante }) {
  const passos = p.journey?.steps ?? [];
  if (!passos.length) return null;

  return (
    <Cartao titulo="Por onde a conversa passou" icone="ti-route">
      <ol className="pipeline">
        {passos.map((passo) => (
          <li key={passo.id} className={passo.completed ? 'feito' : 'pendente'}>
            <i className={`ti ${passo.completed ? 'ti-circle-check' : 'ti-circle-dashed'}`} />
            <span className="pipeline-etapa">{ETAPAS[passo.stage] ?? passo.stage}</span>
            <span className="nota-pe">
              {passo.completedAt
                ? new Date(passo.completedAt).toLocaleString('pt-BR')
                : 'em aberto'}
            </span>
          </li>
        ))}
      </ol>
    </Cartao>
  );
}

/**
 * O consentimento, que neste produto ocupa o lugar dos "Critérios de Eliminação"
 * do sistema irmão: é o portão que decide se a pessoa entra.
 *
 * Na esquerda pela mesma razão do `Pipeline`, e por uma a mais: o cartão diz que
 * o consentimento é **da pessoa**, e o lado da tela que fala da pessoa é este.
 * Ler isso ao lado da análise sugeria que a autorização pertencia a esta
 * participação — que é exatamente o contrário do que a descrição do cartão
 * afirma.
 */
function Consentimento({ participante: p }: { participante: Participante }) {
  return (
    <Cartao
      titulo="Consentimento"
      icone="ti-shield-check"
      descricao="É da pessoa, não da participação. Quem já autorizou numa edição anterior não é perguntado de novo."
    >
      {p.consents.length ? (
        <dl className="ficha ficha-empilhada">
          {p.consents.map((c) => (
            <Linha key={c.id} rotulo={FINALIDADES[c.purpose] ?? c.purpose}>
              {c.revokedAt ? (
                <>
                  <Tag type="danger">revogado</Tag>{' '}
                  <span className="nota-pe">
                    {new Date(c.revokedAt).toLocaleDateString('pt-BR')}
                  </span>
                </>
              ) : c.grantedAt ? (
                <>
                  <Tag type="success">autorizado</Tag>{' '}
                  <span className="nota-pe">
                    {new Date(c.grantedAt).toLocaleDateString('pt-BR')}
                  </span>
                </>
              ) : (
                <Tag type="muted">{c.response ?? 'sem resposta'}</Tag>
              )}
            </Linha>
          ))}
        </dl>
      ) : (
        <div className="aviso">
          <i className="ti ti-file-off" />
          <span>
            Nenhum consentimento registrado.{' '}
            {p.legalBasis && p.legalBasis !== 'consentimento'
              ? `A base legal desta participação é ${p.legalBasis}.`
              : 'A jornada pede a autorização na primeira etapa.'}
          </span>
        </div>
      )}
    </Cartao>
  );
}

/**
 * A barra do material — os botões do sistema irmão ("Depoimento principal",
 * "Ver transcrição"), pedidos pelo Gabriel em 21/10/2026.
 *
 * Até então as perguntas e respostas ocupavam um cartão inteiro na coluna da
 * esquerda, e a conversa completa era um botão dentro de "Documentos". O que a
 * pessoa mandou é para conferir quando a análise levanta dúvida, não para ler
 * sempre: em botões, fica a um clique e as colunas ficam com a identidade e a
 * análise. "Documentos" continua listando tudo — a barra abre o principal.
 */
function BarraDoMaterial({
  participante: p,
  material,
  carregando,
  aoAbrirPeca,
  aoAbrirEntrevista,
  aoAbrirTranscricao,
}: {
  participante: Participante;
  material?: Material;
  carregando: boolean;
  aoAbrirPeca: (p: Peca) => void;
  aoAbrirEntrevista: () => void;
  aoAbrirTranscricao: () => void;
}) {
  const { pode, liberado } = useAuth();
  const pecas = material?.pecas ?? [];
  const pares = material?.perguntasERespostas.length ?? 0;

  // O relato principal é o mais longo: os áudios curtos que sobram como peça são
  // complementos, e não o depoimento.
  const relato = principal(pecas, 'relato_audio', p.event.id, maisLongo);
  const curriculo = principal(pecas, 'curriculo', p.event.id, maisRecente);
  const perfilPublico = principal(pecas, 'linkedin', p.event.id, maisRecente);

  const indisponivel = (motivo: string) => (carregando ? 'Carregando o material…' : motivo);

  return (
    <section className="card barra-material">
      <div className="barra-material-grupo">
        <button
          className="btn btn-secondary"
          disabled={!relato}
          title={
            relato
              ? 'O relato em áudio, transcrito'
              : indisponivel('O relato em áudio ainda não chegou.')
          }
          onClick={() => relato && aoAbrirPeca(relato)}
        >
          <i className="ti ti-microphone" /> Relato principal
        </button>

        <button
          className="btn btn-secondary btn-contorno-info"
          disabled={!pares}
          title={
            pares
              ? 'As perguntas da IA com as respostas, transcritas'
              : indisponivel('Nenhuma pergunta foi respondida ainda.')
          }
          onClick={aoAbrirEntrevista}
        >
          <i className="ti ti-messages" /> Ver entrevista
          {!!pares && <span className="grupo-pecas-conta">{pares}</span>}
        </button>

        <button
          className="btn btn-secondary"
          disabled={!p.conversation}
          title={
            p.conversation
              ? 'A conversa no WhatsApp, como ela aconteceu'
              : 'Esta pessoa ainda não conversou pelo WhatsApp.'
          }
          onClick={aoAbrirTranscricao}
        >
          <i className="ti ti-message-2" /> Ver transcrição
        </button>

        {curriculo && (
          <button
            className="btn btn-secondary"
            title="O texto lido do currículo"
            onClick={() => aoAbrirPeca(curriculo)}
          >
            <i className="ti ti-file-text" /> Currículo
          </button>
        )}

        {perfilPublico && (
          <button
            className="btn btn-secondary"
            title="O que foi lido do perfil público"
            onClick={() => aoAbrirPeca(perfilPublico)}
          >
            <i className="ti ti-brand-linkedin" /> Perfil público
          </button>
        )}
      </div>

      {pode('recommendations.read') && liberado('jornadas') && (
        <div className="barra-material-grupo">
          <Link className="btn btn-secondary" to={`/eventos/${p.event.id}/recomendacoes`}>
            <i className="ti ti-users-group" /> Conexões do evento
          </Link>
        </div>
      )}
    </section>
  );
}

/** A peça de uma fonte que a barra abre: deste evento antes de outro, e pela ordem dada. */
function principal(
  pecas: Peca[],
  fonte: string,
  eventoAtual: string,
  ordem: (a: Peca, b: Peca) => number,
): Peca | null {
  const daFonte = pecas.filter((peca) => peca.fonte === fonte);
  const desteEvento = daFonte.filter((peca) => peca.eventId === eventoAtual);
  return [...(desteEvento.length ? desteEvento : daFonte)].sort(ordem)[0] ?? null;
}

const maisRecente = (a: Peca, b: Peca) =>
  new Date(b.criadaEm).getTime() - new Date(a.criadaEm).getTime();

const maisLongo = (a: Peca, b: Peca) => b.texto.length - a.texto.length;

/**
 * A entrevista: as perguntas da etapa 6 com as respostas — as "Informações
 * Objetivas" do sistema irmão, que lá são um bloco fixo configurável por vaga e
 * aqui são as perguntas que a IA escolheu pela lacuna que mais atrapalhava o
 * cruzamento. Em modal desde 21/10/2026; antes era um cartão da coluna da
 * esquerda.
 */
function EntrevistaModal({
  material,
  eventoAtual,
  aoFechar,
}: {
  material: Material;
  eventoAtual: string;
  aoFechar: () => void;
}) {
  const pares = material.perguntasERespostas;

  // Desde 28/10/2026 sai uma pergunta por tema. As entrevistas de antes eram
  // escolhidas pela lacuna do perfil, e a descrição diz qual foi o caso.
  const eventos = new Set(pares.map((qa) => qa.eventId)).size;
  const quantas = `${pares.length} ${pares.length === 1 ? 'pergunta' : 'perguntas'}`;
  const porTema = pares.some((qa) => qa.tema);
  const descricao =
    `${eventos > 1 ? `${quantas}, em ${eventos} eventos` : quantas}. ` +
    (porTema
      ? 'Uma por tema — projetos, projeto principal e valores —, depois do relato'
      : 'Cada uma foi escolhida pela lacuna que mais atrapalhava o cruzamento') +
    '; as respostas chegaram em áudio e aparecem transcritas.';

  return (
    <Modal titulo="A entrevista" descricao={descricao} aoFechar={aoFechar} largura={720}>
      {pares.map((qa, i) => (
        <div key={i} className="par-pergunta">
          <div className="pergunta">
            <span className="pergunta-numero">{i + 1}</span>
            <span>{qa.pergunta}</span>
            {qa.tema && NOMES_DOS_TEMAS[qa.tema] && <Tag>{NOMES_DOS_TEMAS[qa.tema]}</Tag>}
            {qa.eventId !== eventoAtual && <DeOutroEvento evento={qa.evento} />}
          </div>
          <p className="resposta texto-corrido">{qa.resposta}</p>
        </div>
      ))}

      {/*
        O limite do pareamento, dito a quem lê e não só no código — e só quando
        ele vale. Desde 13/10/2026 cada pergunta guarda a hora em que foi feita, e
        a resposta é da última pergunta anterior a ela. As jornadas de antes não
        têm a hora, e nelas o par é montado pela ORDEM: quem confere um perfil
        precisa saber disso antes de concluir que o modelo errou.
      */}
      {material.pareamento === 'ordem' && (
        <div className="nota-pe" style={{ marginTop: 10 }}>
          <i className="ti ti-info-circle" /> O par é montado pela ordem, não por uma ligação entre
          os dois. Se a pessoa mandou duas mensagens para uma pergunta, o alinhamento pode estar
          deslocado daí para baixo — a conversa completa mostra a ordem real.
        </div>
      )}
    </Modal>
  );
}

/** A etiqueta de material que veio de outra participação da mesma pessoa. */
function DeOutroEvento({ evento }: { evento: string }) {
  return (
    <Tag type="info" icon="ti-calendar-event" title={`Veio da participação em ${evento}`}>
      {evento}
    </Tag>
  );
}

/** O "Histórico de Vagas" do sistema irmão — aqui, os outros eventos da pessoa. */
function OutrosEventos({ participante: p }: { participante: Participante }) {
  return (
    <Cartao
      titulo="Outros eventos"
      icone="ti-history"
      descricao="O perfil é da pessoa, não da participação — quem voltou chega com o que a conversa anterior construiu."
    >
      {p.participations.length ? (
        <ul className="lista-eventos">
          {p.participations.map((outra) => (
            <li key={outra.id}>
              <Link to={`/participantes/${outra.id}`}>{outra.event.name}</Link>
              <span className="nota-pe">
                {outra.event.startDate
                  ? new Date(outra.event.startDate).toLocaleDateString('pt-BR')
                  : 'sem data'}{' '}
                · {outra.event.status}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="nota-pe">Primeira vez que esta pessoa participa de um evento seu.</div>
      )}
    </Cartao>
  );
}

/**
 * O "Sobre a Vaga" do sistema irmão — e do mesmo tamanho: uma frase e quatro
 * linhas com ícone.
 *
 * Até 16/10/2026 o cartão listava objetivo por objetivo, e virava a coisa mais
 * comprida da coluna para repetir o que a aba do evento e o cartão "Aderência,
 * objetivo por objetivo" já mostram. Aqui fica só a contagem, com o caminho
 * para o cadastro; a lista inteira mora onde se edita.
 */
function SobreOEvento({ participante: p }: { participante: Participante }) {
  const e = p.event;
  const contagem = contarObjetivos(p.analise.objetivos);
  const local = cidadeDoEvento(e);

  return (
    <Cartao titulo="Sobre o evento">
      {e.shortDescription && <p className="sobre-texto">{e.shortDescription}</p>}

      <ul className="sobre-linhas">
        {local && (
          <li>
            <i className="ti ti-map-pin" /> {local}
          </li>
        )}
        <li>
          <i className="ti ti-calendar" /> {formatarPeriodo(e.startDate, e.endDate)}
        </li>
        <li>
          <i className="ti ti-target" />{' '}
          {contagem ? (
            <Link to={`/eventos/${e.id}?aba=objetivos`}>{contagem}</Link>
          ) : (
            /* Sem cadastro não há aderência para medir — e quem lê precisa saber
               por que o índice está vazio, não só que está. */
            <span>
              Sem objetivos nem temas — sem eles não há aderência para medir.{' '}
              <Link to={`/eventos/${e.id}?aba=objetivos`}>Cadastrar</Link>
            </span>
          )}
        </li>
        <li>
          <i className="ti ti-flag" /> {ROTULOS_STATUS[e.status] ?? e.status}
        </li>
      </ul>
    </Cartao>
  );
}

/** "3 objetivos · 3 públicos · 4 temas" — só o que existe, na ordem do cadastro. */
function contarObjetivos(objetivos: Analise['objetivos']) {
  const nomes: Record<string, [string, string]> = {
    objetivo: ['objetivo', 'objetivos'],
    publico: ['público', 'públicos'],
    tema: ['tema', 'temas'],
  };

  return Object.entries(nomes)
    .map(([tipo, [um, varios]]) => {
      const n = objetivos.filter((o) => o.kind === tipo).length;
      return n ? `${n} ${n === 1 ? um : varios}` : null;
    })
    .filter(Boolean)
    .join(' · ');
}

/**
 * O nome de cada grupo de material, pelo `fonte` que `material.ts` atribui.
 *
 * ⚠️ **O rótulo da peça é singular e descreve UMA peça** ("o relato que ela
 * gravou, transcrito"). Ele está certo sozinho e vira parede repetido: o perfil
 * real tem quinze áudios, e a lista era quinze linhas idênticas, metade delas na
 * mesma data. Uma lista em que ninguém distingue um item do outro não é
 * auditável — é só comprimento.
 *
 * Por isso o plural aqui, e por isso o grupo só existe a partir de dois: com uma
 * peça só, o rótulo singular da própria peça é mais preciso que qualquer título
 * de grupo, e um menu que abre para revelar uma linha é cerimônia à toa.
 */
const GRUPOS_DE_MATERIAL: Record<string, { plural: string; icone: string }> = {
  relato_audio: { plural: 'Relatos em áudio, transcritos', icone: 'ti-waveform' },
  curriculo: { plural: 'Currículos enviados', icone: 'ti-file-text' },
  linkedin: { plural: 'Perfis profissionais públicos', icone: 'ti-link' },
  resposta_texto: { plural: 'Respostas escritas', icone: 'ti-message-dots' },
};

const ICONE_PADRAO = 'ti-paperclip';

/**
 * O começo do texto, para linhas de mesmo rótulo deixarem de ser indistinguíveis.
 *
 * É o que permite escolher qual áudio abrir sem abrir os quinze. Vem de
 * `peca.texto`, que é o mesmo texto que o modelo recebeu.
 */
function trecho(texto: string, limite = 68) {
  const limpo = texto.replace(/\s+/g, ' ').trim();
  return limpo.length > limite ? `${limpo.slice(0, limite).trimEnd()}…` : limpo;
}

/** Data e hora curtas: várias peças caem no mesmo dia, e só a hora as separa. */
function dataHoraCurta(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Todo o material, peça por peça. A conversa completa abre pela barra do topo. */
function Documentos({
  material,
  eventoAtual,
  aoAbrirPeca,
}: {
  material?: Material;
  eventoAtual: string;
  aoAbrirPeca: (p: Peca) => void;
}) {
  const pecas = material?.pecas ?? [];
  const [abertos, setAbertos] = useState<string[]>([]);

  const alternar = (chave: string) =>
    setAbertos((atual) =>
      atual.includes(chave) ? atual.filter((c) => c !== chave) : [...atual, chave],
    );

  // Agrupa preservando a ordem de chegada do primeiro item de cada grupo — a
  // mesma ordem cronológica que `montarMaterial` devolve.
  const grupos: { chave: string; pecas: Peca[] }[] = [];
  for (const peca of pecas) {
    const grupo = grupos.find((g) => g.chave === peca.fonte);
    if (grupo) grupo.pecas.push(peca);
    else grupos.push({ chave: peca.fonte, pecas: [peca] });
  }

  return (
    <Cartao titulo="Documentos e material" icone="ti-paperclip">
      {grupos.length ? (
        <ul className="lista-pecas">
          {grupos.map((grupo) => {
            const desenho = GRUPOS_DE_MATERIAL[grupo.chave];
            const icone = desenho?.icone ?? ICONE_PADRAO;

            // Peça única: o rótulo dela já é singular e certo. Abre direto.
            if (grupo.pecas.length === 1) {
              const peca = grupo.pecas[0];
              return (
                <li key={grupo.chave}>
                  <button className="link-peca" onClick={() => aoAbrirPeca(peca)}>
                    <i className={`ti ${icone}`} />
                    {peca.rotulo}
                  </button>
                  <span className="nota-pe">
                    {new Date(peca.criadaEm).toLocaleDateString('pt-BR')}
                  </span>
                  {peca.eventId !== eventoAtual && <DeOutroEvento evento={peca.evento} />}
                </li>
              );
            }

            const aberto = abertos.includes(grupo.chave);

            return (
              <li key={grupo.chave} className="grupo-pecas">
                <button
                  className="grupo-pecas-cabeca"
                  onClick={() => alternar(grupo.chave)}
                  aria-expanded={aberto}
                >
                  <i className={`ti ${icone}`} />
                  <span className="grupo-pecas-nome">
                    {desenho?.plural ?? 'Material enviado por ela'}
                  </span>
                  <span className="grupo-pecas-conta">{grupo.pecas.length}</span>
                  <i className={`ti ti-chevron-${aberto ? 'up' : 'down'} grupo-pecas-seta`} />
                </button>

                {aberto && (
                  <ul className="grupo-pecas-itens">
                    {grupo.pecas.map((peca, i) => (
                      <li key={i}>
                        <button className="link-peca" onClick={() => aoAbrirPeca(peca)}>
                          <span className="grupo-pecas-quando">{dataHoraCurta(peca.criadaEm)}</span>
                          <span className="grupo-pecas-trecho">{trecho(peca.texto)}</span>
                        </button>
                        {peca.eventId !== eventoAtual && <DeOutroEvento evento={peca.evento} />}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="nota-pe" style={{ marginTop: 10 }}>
          Nenhum material enviado ainda.
        </div>
      )}

      {/*
        Dito, e não só escondido: a síntese leu estas peças. Sem a frase, quem
        confere a síntese contra o material acharia nela coisa que "ninguém
        mandou" e concluiria que o modelo inventou.
      */}
      {!!material?.ocultas && (
        <div className="nota-pe" style={{ marginTop: 10 }}>
          <i className="ti ti-lock" /> {material.ocultas}{' '}
          {material.ocultas === 1 ? 'item vem' : 'itens vêm'} de eventos a que você não tem acesso
          e não {material.ocultas === 1 ? 'aparece' : 'aparecem'} aqui — mas a síntese os leu.
        </div>
      )}
    </Cartao>
  );
}

/* ─────────── Coluna direita: a análise ─────────── */

/**
 * ⭐ O Parecer Executivo — a síntese para decidir em segundos.
 *
 * A **sugestão** é regra fixa, calculada no servidor. Os três textos curtos são da
 * IA. É a divisão que o sistema irmão faz, e o motivo é o mesmo: o que é decisão
 * tem de ser auditável, o que é leitura pode ser prosa.
 */
function ParecerExecutivo({ participante: p }: { participante: Participante }) {
  const notas = p.profile?.synthesisNotes;
  const sugestao = SUGESTOES[p.analise.sugestao];

  return (
    <Cartao titulo="Parecer executivo" icone="ti-stars" destaque>
      <div className="parecer-sugestao">
        <Tag type={sugestao.cor}>{sugestao.rotulo}</Tag>
        <span className="nota-pe">{sugestao.ajuda}</span>
      </div>

      {notas?.resumoExecutivo ? (
        <>
          <p className="parecer-resumo">{notas.resumoExecutivo}</p>

          <div className="parecer-par">
            <div>
              <div className="parecer-rotulo">
                <i className="ti ti-trending-up" /> Maior diferencial
              </div>
              <div>{notas.maiorDiferencial}</div>
            </div>
            <div>
              <div className="parecer-rotulo atencao">
                <i className="ti ti-alert-triangle" /> Principal ponto de atenção
              </div>
              <div>{notas.pontoAtencao}</div>
            </div>
          </div>
        </>
      ) : (
        <SemSintese perfil={p.profile} oQue="o parecer" />
      )}

      {!!notas?.pontosDeAtencao?.length && (
        <div className="aviso" style={{ marginTop: 14 }}>
          <i className="ti ti-alert-triangle" />
          <span>
            <strong>
              {notas.pontosDeAtencao.length}{' '}
              {notas.pontosDeAtencao.length === 1 ? 'ponto' : 'pontos'} de atenção
            </strong>{' '}
            registrados — leia antes de aprovar qualquer recomendação.
          </span>
        </div>
      )}
    </Cartao>
  );
}

/**
 * A chance de conexão — no lugar do Índice de aderência desde 27/10/2026.
 *
 * Conta o que o cruzamento pontuou, e por isso pode ser conferida: cada conexão
 * contada está em "Conexões indicadas" ou na página de conexões do evento.
 */
function ChanceDeConexao({ participante: p }: { participante: Participante }) {
  const { chance } = p.analise;

  return (
    <Cartao
      titulo="Chance de conexão"
      icone="ti-heart-handshake"
      descricao="Quantas pessoas deste evento o cruzamento encontrou para ela acima do corte, e a melhor nota. É contagem do que foi pontuado, não estimativa."
    >
      {chance.motivo !== null ? (
        <div className="aviso">
          <i className="ti ti-help-circle" />
          <span>{MOTIVOS_SEM_CHANCE[chance.motivo]}</span>
        </div>
      ) : (
        <>
          <div className="indice-bloco" style={{ marginBottom: 10 }}>
            <div className={`indice-numero faixa-${corDaChance(chance)}`}>{chance.possiveis}</div>
            <div>
              <div className="indice-rotulo">
                {chance.possiveis === 1 ? 'conexão possível' : 'conexões possíveis'}
                {chance.melhor !== null && <>, a melhor com {chance.melhor}</>}
              </div>
              <div className="nota-pe">
                {chance.avaliados} {chance.avaliados === 1 ? 'pessoa avaliada' : 'pessoas avaliadas'}{' '}
                · corte do evento {chance.corte}
              </div>
            </div>
          </div>
          {chance.possiveis === 0 && (
            <div className="nota-pe">
              Ninguém deste evento passou do corte por enquanto. Muda quando mais participantes
              terminarem a jornada.
            </div>
          )}
        </>
      )}
    </Cartao>
  );
}

function AnalisePsicologica({ participante: p }: { participante: Participante }) {
  const psi = p.profile?.psychologicalProfile;

  if (!psi) {
    return (
      <Cartao titulo="Análise psicológica" icone="ti-brain">
        <div className="aviso">
          <i className="ti ti-help-circle" />
          <span>
            {p.profile?.synthesizedAt
              ? 'A síntese rodou e concluiu que a conversa não sustentava uma leitura confiável — respostas curtas não dizem como alguém trabalha. Isto não é erro: preferimos campo vazio a conclusão confiante sobre nada.'
              : 'A síntese do encerramento ainda não rodou. É ela que produz esta leitura.'}
          </span>
        </div>
      </Cartao>
    );
  }

  const porcento = Math.round(psi.confianca * 100);

  return (
    <Cartao titulo="Análise psicológica" icone="ti-brain">
      {/*
        ⚠️ A confiança fica no TOPO e junto do conteúdo, e isso é a decisão, não o
        layout. O comentário do schema diz exatamente o que temer: "ninguém abre o
        JSON para conferir o campo de confiança antes de confiar nele". Num canto
        da tela ela teria o mesmo destino.
      */}
      <div className="aviso aviso-info" style={{ marginBottom: 14 }}>
        <i className="ti ti-info-circle" />
        <span>
          Inferência sobre uma conversa de WhatsApp — <strong>não é teste aplicado</strong>, e não
          existe perfil melhor ou pior. O próprio modelo avaliou que o material sustenta isto em{' '}
          <strong>{porcento}%</strong>.{' '}
          {porcento < 50 && 'Abaixo da metade, trate como pista e não como descrição.'}
        </span>
      </div>

      {psi.perfil ? (
        <ComoTrabalha psi={psi} pontuacao={p.analise.pontuacaoComportamental} />
      ) : (
        <ComoTrabalhavaAntes psi={psi} />
      )}
    </Cartao>
  );
}

/** Abaixo disto, arquétipo e MBTI aparecem como hipótese. Ver `ComoTrabalha`. */
const CONFIANCA_DE_CONCLUSAO = 0.7;

function EtiquetaDeHipotese() {
  return (
    <span style={{ marginLeft: 8 }}>
      <Tag title="A conversa ainda não sustenta isto como conclusão — leia como pista.">
        hipótese
      </Tag>
    </span>
  );
}

/** Os quatro blocos do sistema irmão: perfil, motivações, comportamental, pontuação. */
function ComoTrabalha({ psi, pontuacao }: { psi: PerfilPsicologico; pontuacao: number | null }) {
  const perfil = psi.perfil!;
  const motivacoes = psi.motivacoes;
  const comportamental = psi.comportamental ?? {};
  /**
   * Arquétipo e MBTI são hipótese abaixo de 70% (21/10/2026): quatro letras ao
   * lado de um nome parecem resultado de teste, e com pouca conversa não são.
   */
  const hipotese = psi.confianca < CONFIANCA_DE_CONCLUSAO;

  return (
    <>
      <div className="sub-bloco">
        <div className="sub-titulo">Perfil</div>
        <dl className="ficha">
          <Linha rotulo="Arquétipo">
            <Tag type="info">{perfil.arquetipo}</Tag>
            {hipotese && <EtiquetaDeHipotese />}
          </Linha>
          <Linha rotulo="Aproximação de MBTI">
            <span className="mono">{perfil.mbti}</span>
            {hipotese && <EtiquetaDeHipotese />}
          </Linha>
          <Linha rotulo="Inteligências predominantes">{perfil.inteligencias}</Linha>
          <Linha rotulo="Estilo de liderança">{perfil.lideranca}</Linha>
        </dl>
      </div>

      {motivacoes && (
        <div className="sub-bloco">
          <div className="sub-titulo">Motivações intrínsecas</div>
          <div className="notas">
            <BarraDeNota rotulo="Autonomia" nota={motivacoes.autonomia} />
            <BarraDeNota rotulo="Competência" nota={motivacoes.competencia} />
            <BarraDeNota rotulo="Pertencimento" nota={motivacoes.pertencimento} />
          </div>
          <dl className="ficha" style={{ marginTop: 10 }}>
            <Linha rotulo="Motivador principal">
              <Tag>{motivacoes.motivadorPrincipal}</Tag>
            </Linha>
          </dl>
          <p className="nota-pe" style={{ marginTop: 6 }}>
            {motivacoes.obs}
          </p>
        </div>
      )}

      <div className="sub-bloco">
        <div className="sub-titulo">
          Análise comportamental
          {pontuacao !== null && <span className="sub-pontuacao">{pontuacao}/100</span>}
        </div>
        <div className="notas">
          {DIMENSOES.map(({ chave, rotulo }) => {
            const d = comportamental[chave];
            if (!d) return null;
            return <BarraDeNota key={chave} rotulo={rotulo} nota={d.nota} obs={d.obs} />;
          })}
        </div>
      </div>

      {psi.compatibilidade && <ComoTrabalhaComOsOutros c={psi.compatibilidade} />}
    </>
  );
}

/**
 * O jeito de trabalhar que o cruzamento usa para casar pessoas (27/10/2026).
 *
 * Só o que a conversa mostrou: campo vazio não aparece, e o rodapé diz que isso
 * é "não sabemos", não incompatibilidade — a mesma leitura que o cruzamento faz.
 */
function ComoTrabalhaComOsOutros({ c }: { c: Compatibilidade }) {
  const linhas: [string, string][] = (
    [
      ['Ritmo', c.ritmo],
      ['Como decide', c.decisao],
      ['Comunicação', c.comunicacao],
      ['Risco', c.risco],
    ] as [string, string][]
  ).filter(([, texto]) => texto?.trim());

  if (!linhas.length && !c.trabalhaBemCom?.length && !c.atritoCom?.length) return null;

  return (
    <div className="sub-bloco">
      <div className="sub-titulo">Como trabalha com os outros</div>
      <dl className="ficha">
        {linhas.map(([rotulo, texto]) => (
          <Linha key={rotulo} rotulo={rotulo}>
            {texto}
          </Linha>
        ))}
        {!!c.trabalhaBemCom?.length && (
          <Linha rotulo="Trabalha bem com">{c.trabalhaBemCom.join(' · ')}</Linha>
        )}
        {!!c.atritoCom?.length && <Linha rotulo="Dá atrito com">{c.atritoCom.join(' · ')}</Linha>}
      </dl>
      <p className="nota-pe" style={{ marginTop: 6 }}>
        É o que o cruzamento usa para casar jeitos de trabalhar. O que não aparece aqui a conversa
        não mostrou — e o cruzamento lê como desconhecido, não como incompatível.
      </p>
    </div>
  );
}

/**
 * O formato anterior a 12/10/2026 — cinco campos de prosa, sem rótulo nem nota.
 *
 * Perfil sintetizado antes da mudança não tem `perfil` nem `comportamental`.
 * Mostrar o que existe é melhor que um painel vazio, e a nota diz o que falta e
 * como resolver.
 */
function ComoTrabalhavaAntes({ psi }: { psi: PerfilPsicologico }) {
  const linhas: [string, string | undefined][] = [
    ['Comunicação', psi.comunicacao],
    ['Decisão', psi.decisao],
    ['Motivação', psi.motivacao],
    ['Colaboração', psi.colaboracao],
    ['Ritmo', psi.ritmo],
  ];

  return (
    <>
      <div className="nota-pe" style={{ marginBottom: 10 }}>
        <i className="ti ti-history" /> Perfil gravado no formato anterior, em prosa. O arquétipo,
        o MBTI e as oito dimensões aparecem quando a síntese rodar de novo.
      </div>
      <div className="dimensoes">
        {linhas
          .filter(([, texto]) => texto)
          .map(([rotulo, texto]) => (
            <div key={rotulo} className="dimensao">
              <div className="dimensao-rotulo">{rotulo}</div>
              <div className="dimensao-texto">{texto}</div>
            </div>
          ))}
      </div>
    </>
  );
}

/**
 * As conexões desta pessoa — o lugar do "Team Match" do sistema irmão.
 *
 * Lá o bloco compara o candidato com o DNA dos colaboradores do departamento;
 * aqui não existe departamento nem DNA cadastrado, e o equivalente real são as
 * indicações que a fase 4 gerou para ela.
 */
function Conexoes({ participante: p }: { participante: Participante }) {
  const { pode, liberado } = useAuth();
  const podeVer = pode('recommendations.read') && liberado('jornadas');

  const consulta = useQuery({
    queryKey: ['indicacoes', p.event.id, p.id],
    queryFn: () =>
      api<Paginado<Indicacao>>(
        `/events/${p.event.id}/recommendations?participantId=${p.id}&page=1&perPage=10`,
      ),
    enabled: podeVer,
  });

  if (!podeVer) return null;

  const indicacoes = consulta.data?.data ?? [];

  return (
    <Cartao
      titulo="Conexões indicadas"
      icone="ti-users-group"
      descricao="Quem o cruzamento sugeriu para esta pessoa, com o score e a justificativa que ela recebeu."
      acao={
        <Link className="btn btn-secondary btn-pequeno" to={`/eventos/${p.event.id}/recomendacoes`}>
          Ver todas
        </Link>
      }
    >
      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} />}

      {consulta.data &&
        (indicacoes.length ? (
          <ul className="lista-indicacoes">
            {indicacoes.map((r) => (
              <li key={r.id}>
                <div className="indicacao-cabeca">
                  <strong>{r.suggested?.person.fullName ?? '—'}</strong>
                  {r.score !== null && <Tag type="info">{r.score}</Tag>}
                  <Tag type="muted">{r.rotulo}</Tag>
                </div>
                {r.suggested && (
                  <div className="nota-pe">
                    {[r.suggested.jobTitle, r.suggested.company].filter(Boolean).join(' · ')}
                  </div>
                )}
                {r.justification && <p className="texto-corrido">{r.justification}</p>}
              </li>
            ))}
          </ul>
        ) : (
          <div className="nota-pe">
            Nenhuma indicação ainda. O cruzamento precisa de pelo menos duas pessoas com perfil
            suficiente no mesmo evento.
          </div>
        ))}
    </Cartao>
  );
}

/**
 * Os pontos de atenção — o lugar da "Conformidade das Informações" do sistema
 * irmão, e **é menos do que lá**.
 *
 * Lá existe uma chamada de IA dedicada a confrontar currículo × áudio ×
 * entrevista × LinkedIn e listar divergências com gravidade. Aqui as contradições
 * saem da própria síntese, instruída a dizê-las em vez de escolher um lado. A
 * tela diz que é menos, em vez de parecer equivalente.
 */
function Atencao({ perfil }: { perfil: Perfil | null }) {
  const pontos = perfil?.synthesisNotes?.pontosDeAtencao ?? [];
  const assuntos = perfil?.synthesisNotes?.assuntosParaComecar ?? [];

  if (!pontos.length && !assuntos.length) return null;

  return (
    <>
      {!!pontos.length && (
        <Cartao
          titulo="Pontos de atenção"
          icone="ti-alert-triangle"
          descricao="Contradição no material, informação que parece inflada, assunto que a pessoa sinalizou não querer tratar."
        >
          <ul className="lista-atencao">
            {pontos.map((ponto) => (
              <li key={ponto}>
                <i className="ti ti-alert-triangle" />
                <span>{ponto}</span>
              </li>
            ))}
          </ul>
          <div className="nota-pe" style={{ marginTop: 10 }}>
            Estes pontos saem da própria síntese, instruída a dizer a contradição em vez de
            escolher um lado. Não existe aqui uma checagem dedicada que confronte cada fonte com
            as outras.
          </div>
        </Cartao>
      )}

      {!!assuntos.length && (
        <Cartao
          titulo="Assuntos para começar"
          icone="ti-message-dots"
          descricao="O que vale puxar numa conversa com ela. São sementes, não frases prontas — quem vai conversar ainda não foi escolhido."
        >
          <ul className="lista-assuntos">
            {assuntos.map((a) => (
              <li key={a}>
                <i className="ti ti-message-dots" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}
    </>
  );
}

function Resumo({ perfil }: { perfil: Perfil | null }) {
  const resumo = perfil?.aiSummary;
  const trajetoria = (perfil?.trajectory as Bloco | undefined)?.value;
  const sintetizado = !!perfil?.synthesizedAt;

  if (!resumo && !trajetoria) return null;

  return (
    <Cartao
      titulo={sintetizado ? 'Síntese do conjunto' : 'Resumo parcial'}
      icone="ti-file-description"
      descricao={
        sintetizado
          ? 'Uma chamada de IA que viu tudo de uma vez — relato, currículo, perfil público, perguntas e respostas.'
          : /*
               ⚠️ A distinção mais importante desta tela.

               `aiSummary` é escrito por dois caminhos. Durante a coleta cada peça
               SOBRESCREVE o campo com o resumo daquela peça — que pode ser a
               resposta de uma palavra à sétima pergunta. A síntese do fim escreve
               por cima com o resumo do conjunto. `synthesizedAt` separa os dois, e
               mostrá-los com a mesma cara faria a tela mentir em metade dos casos.
             */
            'A jornada ainda não terminou, então este texto é o resumo da ÚLTIMA peça que chegou — não do conjunto.'
      }
    >
      {!sintetizado && (
        <div className="aviso" style={{ marginBottom: 12 }}>
          <i className="ti ti-alert-triangle" />
          <span>
            Não decida nada com este texto ainda. Ele pode descrever só a última resposta.
          </span>
        </div>
      )}

      {resumo && <p className="texto-corrido">{resumo}</p>}

      {typeof trajetoria === 'string' && (
        <div style={{ marginTop: resumo ? 14 : 0 }}>
          <div className="sub-titulo">Trajetória</div>
          <p className="texto-corrido">{trajetoria}</p>
        </div>
      )}
    </Cartao>
  );
}

function Experiencia({ perfil }: { perfil: Perfil | null }) {
  const experiencias = perfil?.participantProfile?.experienciaProfissional ?? [];
  const profissional = (perfil?.professional as Bloco | undefined)?.value as
    | Record<string, string | null>
    | undefined;

  if (!experiencias.length && !profissional) {
    return (
      <Cartao titulo="Experiência profissional" icone="ti-briefcase">
        <SemSintese perfil={perfil} oQue="a experiência" />
      </Cartao>
    );
  }

  return (
    <Cartao titulo="Experiência profissional" icone="ti-briefcase">
      {profissional && (
        <dl className="ficha" style={{ marginBottom: experiencias.length ? 14 : 0 }}>
          <Linha rotulo="Agora">
            {[profissional.cargo, profissional.empresa].filter(Boolean).join(' · ')}
          </Linha>
          <Linha rotulo="Área">{profissional.area}</Linha>
          <Linha rotulo="Senioridade">
            {profissional.senioridade && profissional.senioridade !== 'desconhecida' ? (
              <Tag>{profissional.senioridade}</Tag>
            ) : null}
          </Linha>
        </dl>
      )}

      {!!experiencias.length && (
        <ul className="lista-experiencia">
          {experiencias.map((e, i) => (
            <li key={i}>
              <div className="experiencia-cabeca">
                <strong>{e.cargo}</strong>
                <span className="nota-pe">{e.periodo}</span>
              </div>
              <div>{e.empresa}</div>
              {e.destaques && <p className="texto-corrido">{e.destaques}</p>}
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}

/**
 * O projeto principal, os outros projetos e os valores (28/10/2026).
 *
 * O projeto principal é o centro das conexões, e "o que falta" é o que o
 * cruzamento procura nos outros participantes — por isso aparece inteiro. Cada
 * valor vem com a situação que o mostra, porque é ela que se confere.
 */
function ProjetosEValores({ perfil }: { perfil: Perfil | null }) {
  const identidade = perfil?.participantProfile;
  const principal = identidade?.projetoPrincipal;
  const temPrincipal = !!(principal?.nome || principal?.oQueE);
  const outros = identidade?.outrosProjetos ?? [];
  const valores = identidade?.valores ?? [];
  const procura = tiposConhecidos(identidade?.tiposDeConexao?.procura);
  const atende = tiposConhecidos(identidade?.tiposDeConexao?.atende);
  const temTipos = !!(procura.length || atende.length);

  return (
    <Cartao
      titulo="Projetos e valores"
      icone="ti-target-arrow"
      descricao={
        'Os tipos de conexão são o critério principal: quem atende um tipo que a pessoa ' +
        'procura vai para o topo. Depois vem o projeto principal e o que falta para ele avançar.'
      }
    >
      {!temTipos && !temPrincipal && !outros.length && !valores.length ? (
        <SemSintese perfil={perfil} oQue="os projetos e os valores" />
      ) : (
        <>
          {temTipos && (
            <dl className="ficha" style={{ marginBottom: 14 }}>
              <Linha rotulo="Procura">
                <ListaDeTipos tipos={procura} icone="ti-target" />
              </Linha>
              <Linha rotulo="Atende quem procura">
                <ListaDeTipos tipos={atende} icone="ti-heart-handshake" />
              </Linha>
            </dl>
          )}

          {temPrincipal && principal && (
            <dl className="ficha" style={{ marginBottom: 14 }}>
              <Linha rotulo="Projeto principal">
                <strong>{principal.nome}</strong>
                {principal.oQueE && <p className="texto-corrido">{principal.oQueE}</p>}
              </Linha>
              <Linha rotulo="Em que pé está">{principal.estagio}</Linha>
              <Linha rotulo="O que falta">
                {principal.oQueFalta.length ? (
                  <ul className="lista-simples">
                    {principal.oQueFalta.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </Linha>
            </dl>
          )}

          {!!outros.length && (
            <ul className="lista-experiencia" style={{ marginBottom: valores.length ? 14 : 0 }}>
              {outros.map((projeto, i) => (
                <li key={i}>
                  <div className="experiencia-cabeca">
                    <strong>{projeto.nome}</strong>
                  </div>
                  {projeto.oQueE && <p className="texto-corrido">{projeto.oQueE}</p>}
                </li>
              ))}
            </ul>
          )}

          {!!valores.length && (
            <ul className="lista-experiencia">
              {valores.map((v, i) => (
                <li key={i}>
                  <div className="experiencia-cabeca">
                    <Tag type="info" icon="ti-heart">
                      {v.valor}
                    </Tag>
                  </div>
                  {v.evidencia && <p className="texto-corrido">{v.evidencia}</p>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Cartao>
  );
}

/** Só as chaves que a lista conhece, sem repetir: o perfil é JSON livre. */
function tiposConhecidos(
  itens: { tipo: string; evidencia: string }[] | undefined,
): { tipo: TipoDeConexao; evidencia: string }[] {
  const vistos = new Set<string>();
  return (itens ?? []).flatMap((item) => {
    if (!ehTipoDeConexao(item?.tipo) || vistos.has(item.tipo)) return [];
    vistos.add(item.tipo);
    return [{ tipo: item.tipo, evidencia: item.evidencia ?? '' }];
  });
}

/** Os tipos de um lado, com a frase que sustenta cada um. */
function ListaDeTipos({
  tipos,
  icone,
}: {
  tipos: { tipo: TipoDeConexao; evidencia: string }[];
  icone: string;
}) {
  if (!tipos.length) return <span className="nota-pe">Nenhum dos cinco tipos</span>;

  return (
    <ul className="lista-experiencia">
      {tipos.map((t) => (
        <li key={t.tipo}>
          <div className="experiencia-cabeca">
            <Tag type="info" icon={icone}>
              {rotuloDoTipo(t.tipo)}
            </Tag>
          </div>
          {t.evidencia && <p className="texto-corrido">{t.evidencia}</p>}
        </li>
      ))}
    </ul>
  );
}

function Formacao({ perfil }: { perfil: Perfil | null }) {
  const formacao = perfil?.participantProfile?.formacao ?? [];

  return (
    <Cartao titulo="Formação" icone="ti-school">
      {formacao.length ? (
        <ul className="lista-experiencia">
          {formacao.map((f, i) => (
            <li key={i}>
              <div className="experiencia-cabeca">
                <strong>{f.curso}</strong>
                <span className="nota-pe">{f.periodo}</span>
              </div>
              <div>{f.instituicao}</div>
            </li>
          ))}
        </ul>
      ) : (
        <SemSintese perfil={perfil} oQue="a formação" />
      )}
    </Cartao>
  );
}

/**
 * Os blocos que o cruzamento consome e que não têm paralelo no sistema irmão.
 *
 * `necessidades` e `ofertas` são o par que a fase 4 usa — a necessidade de um
 * contra a oferta do outro — e pesam mais que todo o resto na completude. Lá não
 * existe equivalente, porque uma vaga não "oferece" nada a um candidato.
 */
function BlocosRestantes({ perfil }: { perfil: Perfil | null }) {
  if (!perfil) return null;

  const restantes = BLOCOS.filter((b) => !BLOCOS_COM_SECAO.includes(b.coluna))
    .map((b) => ({ ...b, bloco: perfil[b.coluna] as Bloco | undefined }))
    .filter((b) => b.bloco?.value !== undefined && b.bloco?.value !== null);

  if (!restantes.length) return null;

  const quebra = perfil.completenessBreakdown;
  const faltando = quebra
    ? BLOCOS.filter((b) => quebra[b.chave] && !quebra[b.chave]!.preenchido).map((b) => b.rotulo)
    : [];

  return (
    <Cartao
      titulo="O que o cruzamento usa"
      icone="ti-arrows-shuffle"
      descricao="Cada bloco guarda de onde veio e com que confiança — é o que permite desconfiar de um item sem desconfiar do perfil inteiro."
    >
      <div className="blocos">
        {restantes.map(({ chave, rotulo, bloco }) => (
          <div key={chave} className="bloco">
            <div className="bloco-cabeca">
              <span className="bloco-rotulo">
                {rotulo}
              </span>
              <span className="bloco-procedencia">
                {FONTES[bloco!.source] ?? bloco!.source}
                {' · '}
                {Math.round(bloco!.confidence * 100)}% de confiança
              </span>
            </div>
            <Valor valor={bloco!.value} />
          </div>
        ))}
      </div>

      {/* A resposta para "o que ainda não sabemos", que é metade da pergunta. */}
      {quebra && (
        <div className="nota-pe" style={{ marginTop: 12 }}>
          Ainda sem informação: {faltando.length ? faltando.join(', ') : 'nada — o perfil está completo'}.
        </div>
      )}
    </Cartao>
  );
}

function Etiquetas({ participante: p }: { participante: Participante }) {
  if (!p.tags.length) return null;

  return (
    <Cartao
      titulo="Etiquetas"
      icone="ti-tags"
      descricao="A origem importa: etiqueta de IA é inferência, etiqueta manual é afirmação de alguém."
    >
      <div className="valor-lista">
        {p.tags.map((t) => (
          <Tag
            key={t.id}
            type={t.source === 'manual' ? 'info' : 'muted'}
            title={`${t.source}${
              t.confidence ? ` · ${Math.round(Number(t.confidence) * 100)}% de confiança` : ''
            }`}
          >
            {t.tag}
          </Tag>
        ))}
      </div>
    </Cartao>
  );
}

function NotasInternas({ participante: p }: { participante: Participante }) {
  const { pode } = useAuth();
  const cliente = useQueryClient();
  const [texto, setTexto] = useState(p.notes ?? '');
  const podeEditar = pode('participants.update');

  const salvar = useMutation({
    mutationFn: () =>
      api(`/participants/${p.id}`, { method: 'PATCH', body: { notes: texto.trim() || null } }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['participante', p.id] }),
  });

  return (
    <Cartao
      titulo="Notas internas"
      icone="ti-notes"
      descricao="Só a equipe vê. Não entra em prompt nenhum nem em mensagem para a pessoa."
    >
      {podeEditar ? (
        <>
          <textarea
            className="form-input"
            rows={4}
            maxLength={5000}
            placeholder="O que a equipe precisa saber sobre esta pessoa…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              className="btn btn-primary"
              disabled={salvar.isPending || texto === (p.notes ?? '')}
              onClick={() => salvar.mutate()}
            >
              {salvar.isPending ? 'Salvando…' : 'Salvar nota'}
            </button>
          </div>
          {salvar.isError && <ErroBox erro={salvar.error} />}
        </>
      ) : p.notes ? (
        <p className="texto-corrido">{p.notes}</p>
      ) : (
        <div className="nota-pe">Nenhuma nota.</div>
      )}
    </Cartao>
  );
}

/* ─────────── Rodapé ─────────── */

/**
 * ⚠️ **Deixou de ser formalidade em 12/10/2026.**
 *
 * Enquanto o perfil psicológico era prosa sobre comportamento, este texto seria
 * zelo. Com arquétipo e MBTI na tela, ele é a frase que impede o rótulo de ser
 * lido como laudo — e quem lê um relatório com "INTJ" escrito merece saber de
 * onde isso veio.
 *
 * Fixo, num lugar só, sempre visível. No sistema irmão ele é obrigatório em tela
 * e em PDF.
 */
function Consideracoes() {
  return (
    <aside className="consideracoes">
      <div className="consideracoes-titulo">
        <i className="ti ti-scale" /> Considerações sobre esta análise
      </div>
      <p>
        Este relatório é <strong>apoio à decisão</strong>, não um veredito. Tudo que aparece como
        análise foi inferido de uma conversa por WhatsApp, de um currículo e de um perfil público
        — material que a própria pessoa escolheu enviar, e que descreve o que ela quis contar.
      </p>
      <p>
        O <strong>arquétipo</strong> e a <strong>aproximação de MBTI</strong> são leituras do
        estilo observado no material, <strong>não instrumentos aplicados</strong>: ninguém
        respondeu a um inventário de personalidade. Não existe perfil melhor ou pior — existe
        perfil que combina com outro. As notas de 0 a 10 vêm sempre com a observação que as
        sustenta; leia a observação antes da nota.
      </p>
      <p>
        O <strong>índice de aderência</strong> e a <strong>sugestão do parecer</strong> são contas
        determinísticas sobre o que foi coletado, com pesos que ainda não foram calibrados com
        dados de evento real. Um índice alto sobre um perfil pouco preenchido não é mostrado, de
        propósito.
      </p>
    </aside>
  );
}

/* ─────────── Modais ─────────── */

/**
 * A conversa completa, em modal — como a "Transcrição da Entrevista" do sistema
 * irmão.
 */
function TranscricaoModal({
  conversationId,
  material,
  aoFechar,
}: {
  conversationId: string;
  material?: Material;
  aoFechar: () => void;
}) {
  const { pode, liberado } = useAuth();
  const podeVer = pode('conversations.read') && liberado('jornadas');

  const consulta = useQuery({
    queryKey: ['conversa', conversationId, 'mensagens'],
    queryFn: () =>
      api<{ data: Mensagem[]; temMais: boolean }>(`/conversations/${conversationId}/mensagens`),
    enabled: podeVer,
  });

  return (
    <Modal
      titulo="A conversa, como ela aconteceu"
      descricao="Tudo que foi enviado e recebido, em ordem. O áudio aparece transcrito — é o único lugar do produto onde o texto que saiu do Whisper pode ser lido."
      aoFechar={aoFechar}
      largura={760}
    >
      {!podeVer ? (
        <div className="aviso">
          <i className="ti ti-eye-off" />
          <span>
            A transcrição é a conversa, então exige <code>conversations.read</code> e a jornada
            liberada.
          </span>
        </div>
      ) : consulta.isPending ? (
        <Carregando />
      ) : consulta.isError ? (
        <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />
      ) : (
        <>
          {consulta.data!.temMais && (
            <div className="nota-pe" style={{ marginBottom: 10 }}>
              Mostrando as mensagens mais recentes. Para o histórico inteiro, abra em{' '}
              <Link to="/conversas">WhatsApp</Link>.
            </div>
          )}
          <div className="transcricao-jornada">
            {consulta.data!.data.map((m) => (
              <Balao key={m.id} mensagem={m} />
            ))}
          </div>

          {!!material?.perguntasERespostas.length && (
            <div className="nota-pe" style={{ marginTop: 12 }}>
              <i className="ti ti-info-circle" /> As {material.perguntasERespostas.length}{' '}
              perguntas pareadas com as respostas estão em "Ver entrevista", no topo do relatório.
              {material.pareamento === 'ordem' &&
                ' O par é montado pela ordem — é esta conversa que mostra a ordem real.'}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function PecaModal({ peca, aoFechar }: { peca: Peca; aoFechar: () => void }) {
  return (
    <Modal
      titulo={peca.rotulo}
      descricao={
        peca.mediaType === 'AUDIO'
          ? 'Transcrito pelo Whisper. Nome próprio que não esteja no cadastro pode sair errado — é limite de desenho, não defeito.'
          : `Recebido em ${new Date(peca.criadaEm).toLocaleString('pt-BR')}.`
      }
      aoFechar={aoFechar}
      largura={720}
    >
      <p className="texto-corrido">{peca.texto}</p>
    </Modal>
  );
}

/* ─────────── Peças pequenas ─────────── */

/**
 * O que dizer quando a seção depende da síntese.
 *
 * Dois estados diferentes, e confundi-los faz a tela parecer quebrada: a síntese
 * nunca rodou, ou rodou antes de o campo existir (12/10/2026). O segundo caso tem
 * conserto — re-sintetizar — e o primeiro só tem espera.
 */
function SemSintese({ perfil, oQue }: { perfil: Perfil | null; oQue: string }) {
  if (!perfil?.synthesizedAt) {
    return (
      <div className="nota-pe">
        A síntese do encerramento ainda não rodou — é ela que consolida {oQue}.
      </div>
    );
  }

  return (
    <div className="nota-pe">
      <i className="ti ti-history" /> Este perfil foi sintetizado antes de {oQue} passar a ser
      extraído. Re-sintetizar preenche.
    </div>
  );
}

function Valor({ valor }: { valor: unknown }): ReactNode {
  if (valor === null || valor === undefined) return null;

  if (Array.isArray(valor)) {
    return (
      <Pilulas
        itens={valor.map((item) =>
          typeof item === 'object' ? JSON.stringify(item) : String(item),
        )}
        teto={10}
      />
    );
  }

  if (typeof valor === 'object') {
    const entradas = Object.entries(valor as Record<string, unknown>).filter(
      ([, v]) => v !== null && v !== undefined && v !== '',
    );

    return (
      <dl className="valor-objeto">
        {entradas.map(([chave, v]) => (
          <div key={chave}>
            <dt>{chave}</dt>
            <dd>
              {Array.isArray(v)
                ? v.join(', ')
                : typeof v === 'object'
                  ? JSON.stringify(v)
                  : String(v)}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return <div className="valor-texto">{String(valor)}</div>;
}

/** Linha de ficha que desaparece quando não há valor — campo vazio não informa nada. */
function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '' || children === false) {
    return null;
  }

  return (
    <div>
      <dt>{rotulo}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function iniciais(nome: string) {
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}

/* ─────────── Direitos do titular (LGPD) ─────────── */

/**
 * Exportar e excluir os dados da PESSOA — em todos os eventos da organização.
 *
 * Até 13/10/2026 a rota de exclusão existia e não tinha botão, e a exportação nem
 * existia. As duas atravessam todos os eventos da pessoa: quem tem acesso restrito
 * a alguns eventos recebe 403 da API, e a tela não esconde isso — mostra o motivo.
 */
function AcoesDoTitular({ participante: p }: { participante: Participante }) {
  const { pode } = useAuth();
  const [excluindo, setExcluindo] = useState(false);

  const exportar = useMutation({
    mutationFn: () => api<unknown>(`/people/${p.personId}/exportacao`),
    onSuccess: (dados) => {
      // Arquivo, e não tela: é o que se entrega ao titular, e o que a
      // portabilidade pede — legível por máquina.
      const blob = new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dados-do-titular-${p.personId}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  if (!pode('participants.read_pii')) return null;

  return (
    <>
      <button
        className="btn btn-secondary"
        onClick={() => exportar.mutate()}
        disabled={exportar.isPending}
        title="Tudo o que a organização guarda sobre esta pessoa, em todos os eventos, num arquivo JSON"
      >
        <i className="ti ti-download" /> {exportar.isPending ? 'Exportando…' : 'Exportar dados (LGPD)'}
      </button>
      {pode('participants.delete') && (
        <button className="btn btn-secondary" onClick={() => setExcluindo(true)}>
          <i className="ti ti-trash" /> Excluir titular (LGPD)
        </button>
      )}
      {exportar.isError && <ErroBox erro={exportar.error} />}
      {excluindo && <ExcluirTitular participante={p} aoFechar={() => setExcluindo(false)} />}
    </>
  );
}

/**
 * A exclusão a pedido do titular. Irreversível, e a tela diz isso antes.
 *
 * Duas travas, e as duas são de propósito: o motivo (a prova de que o pedido
 * existiu — a pessoa não tem conta para contar a versão dela) e o nome digitado,
 * que é o que impede o clique no perfil errado.
 */
function ExcluirTitular({
  participante: p,
  aoFechar,
}: {
  participante: Participante;
  aoFechar: () => void;
}) {
  const navegar = useNavigate();
  const queryClient = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [nome, setNome] = useState('');

  const excluir = useMutation({
    mutationFn: () =>
      api<{ participacoesEncerradas: number; arquivosQueFalharam: number }>(
        `/people/${p.personId}`,
        { method: 'DELETE', body: { reason: motivo.trim() } },
      ),
    onSuccess: async () => {
      // A lista e o perfil não podem mostrar, do cache, quem acabou de ser apagado.
      await queryClient.invalidateQueries();
      navegar('/participantes');
    },
  });

  const confirmado = nome.trim() === p.fullName.trim() && motivo.trim().length >= 5;

  return (
    <Modal
      titulo="Excluir os dados deste titular"
      descricao="Pedido do próprio titular, pela LGPD. Não se desfaz."
      aoFechar={aoFechar}
    >
      <div className="aviso aviso-danger" style={{ marginBottom: 14 }}>
        <i className="ti ti-alert-triangle" />
        <span>
          Em <strong>todos os eventos</strong> da organização: o nome, o contato, o perfil, a análise,
          as respostas e os áudios, os currículos, a conversa e as indicações são apagados, e as
          mensagens que falavam desta pessoa a outras são substituídas por um aviso. Ficam só os
          registros de consentimento, sem o texto, como prova — e uma marca do telefone, para que
          ninguém a cadastre de novo.
        </span>
      </div>

      <Campo
        label="Como o pedido chegou"
        name="motivo"
        linhas={3}
        valor={motivo}
        aoMudar={setMotivo}
        placeholder="Ex.: pediu por e-mail em 13/10, respondido no mesmo dia."
        ajuda="Fica na auditoria. É a prova de que o pedido existiu."
      />

      <Campo
        label={`Digite "${p.fullName}" para confirmar`}
        name="confirmacao"
        valor={nome}
        aoMudar={setNome}
      />

      {excluir.isError && <ErroBox erro={excluir.error} />}

      <ModalRodape>
        <button type="button" className="btn btn-secondary" onClick={aoFechar}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-perigo"
          disabled={!confirmado || excluir.isPending}
          onClick={() => excluir.mutate()}
        >
          {excluir.isPending ? 'Excluindo…' : 'Excluir definitivamente'}
        </button>
      </ModalRodape>
    </Modal>
  );
}
